import { graphHooks } from '@tunasong/graph-lib/react'
import { Add, Delete, MoreVertIcon } from '@tunasong/icons'
import type { NavigationEntity } from '@tunasong/models'
import { matchAny } from '@tunasong/models'
import { getEntityIcon, usePlugins } from '@tunasong/plugin-lib'
import type { Entity, EntityLink, EntityLink as EntityLinkType, Link as LinkType, Persisted } from '@tunasong/schemas'
import { isEntityLink, isLink } from '@tunasong/schemas'
import {useNavigate, useNavigateToEntity, Button, IconButton, Menu, ListItemIcon, MenuItem, ListItemText} from '@tunasong/ui-lib'
import type { FC, MouseEvent } from 'react'
import React, { useState } from 'react'
import BookmarkDialog from './bookmark-dialog.js'
import styles from './bookmarks.module.css'

export interface BookmarksButtonProps {
  parent: Persisted<Entity>
}

export const BookmarksButton: FC<BookmarksButtonProps> = props => {
  const { parent } = props
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const handleOpen = (ev: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(ev.currentTarget)
  const handleClose = () => setAnchorEl(null)
  const plugins = usePlugins('all')

  const { entities: bookmarks } = graphHooks.useEntityChildren<LinkType | EntityLinkType>({
    parentId: parent.id,
    filter: matchAny(isLink, isEntityLink),
  })

  const [showDialog, setShowDialog] = useState<'internal' | 'external' | false>(false)
  const navigateToEntity = useNavigateToEntity()
  const navigate = useNavigate()

  const [deleteEntity, deleteEntityStatus] = graphHooks.useEntityDelete()

  const deleteBookmark = async (ev: MouseEvent, bookmark: Persisted<LinkType | EntityLink>) => {
    ev.stopPropagation()
    ev.preventDefault()
    await deleteEntity({ entity: bookmark })
  }

  const handleClick = (ev: MouseEvent, bookmark: LinkType | EntityLinkType) => {
    if (isEntityLink(bookmark)) {
      navigateToEntity(bookmark.ref as NavigationEntity)
    }
    if (isLink(bookmark)) {
      void navigate(bookmark.url)
    }
  }

  return (
    <>
      <Button endIcon={<MoreVertIcon />} color="primary" onClick={handleOpen} aria-label="Bookmarks Dropdown Button">
        Bookmarks
      </Button>
      <Menu anchorEl={anchorEl} keepMounted={false} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem
          onClick={() => {
            setAnchorEl(null)
            setShowDialog('internal')
          }}
        >
          <ListItemIcon>
            <Add />
          </ListItemIcon>
          <ListItemText primary={'Internal bookmark...'} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null)
            setShowDialog('external')
          }}
        >
          <ListItemIcon>
            <Add />
          </ListItemIcon>
          <ListItemText primary={'External bookmark...'} />
        </MenuItem>
        <div role="separator" className={styles.separator} />
        {bookmarks.map(bookmark => {
          const name = isEntityLink(bookmark) ? bookmark.ref.name : bookmark.name
          const type = isEntityLink(bookmark) ? bookmark.ref.type : bookmark.type
          const Icon = getEntityIcon({ type }, plugins)
          const href = isLink(bookmark) ? bookmark.url : undefined
          const onClick = isLink(bookmark) ? undefined : (ev: MouseEvent) => handleClick(ev, bookmark)
          const description = isEntityLink(bookmark) ? bookmark.ref.description : bookmark.description
          return (
            <MenuItem key={bookmark.id} onClick={onClick ?? (() => { if (href) {window.open(href, '_blank')} })}>
              <ListItemIcon>
                <Icon />
              </ListItemIcon>
              <ListItemText primary={name} secondary={description} />
              <IconButton onClick={ev => void deleteBookmark(ev, bookmark)} disabled={deleteEntityStatus.isLoading}>
                <Delete color="var(--tuna-palette-action-disabled)" />
              </IconButton>
            </MenuItem>
          )
        })}
      </Menu>
      {showDialog ? (
        <BookmarkDialog type={showDialog} open={true} parent={parent} onClose={() => setShowDialog(false)} />
      ) : null}
    </>
  )
}

export default BookmarksButton
